/**
 * Asks a clinic member for a fresh second-factor code when the Worker answers step_up_required
 * (AUTH-008). Inviting, changing or removing staff needs a code from the last 15 minutes, not only
 * the one entered at sign-in. Once the code passes, the refused write is sent again, so the screen
 * that made it sees only the answer to the retry.
 *
 * A native modal <dialog>: the browser keeps focus inside it, and Escape cancels the write.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useRef, useState } from "react";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { radius, space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/shadow.stylex";
import { shadow } from "@lattice/tokens/shadow.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { loadSecurity, onStepUp, verifySecondFactor } from "../api.ts";
import { signInWithPasskey } from "../passkey.ts";
import { CodeForm } from "./CodeForm.tsx";
/** What this person can confirm with. */
interface Ways {
  readonly code: boolean;
  readonly passkey: boolean;
}

/** The first thing the dialog says about the choice, which depends on what the person has. */
function choice(ways: Ways | null): string {
  if (ways === null) return "Checking how you can confirm.";
  if (ways.code && ways.passkey) return "Nothing changes until you confirm. Use your passkey, or enter a code from your authenticator app or a backup code.";
  if (ways.passkey) return "Nothing changes until you confirm with your passkey.";
  return "Nothing changes until you enter a code from your authenticator app, or a backup code.";
}
function passkeyProblem(outcome: "unsupported" | "refused" | "failed", hasCode: boolean): string {
  if (outcome === "unsupported") return hasCode ? "This browser can't use passkeys. Enter a code from your app instead." : "This browser can't use passkeys. Sign in on one that can.";
  if (outcome === "refused") return "That passkey belongs to a different account. Use your own.";
  return hasCode ? "That passkey didn't work. Enter a code from your app instead." : "That passkey didn't work. Try again, or sign in again.";
}
export function StepUpDialog() {
  const dialog = useRef<HTMLDialogElement>(null);
  const settle = useRef<((passed: boolean) => void) | null>(null);
  const [ways, setWays] = useState<Ways | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    onStepUp(() => new Promise<boolean>(resolve => {
      settle.current = resolve;
      setProblem(null);
      setWays(null);
      /* If the Worker cannot say, offer both rather than leave a passkey-only person with a code form. */
      void loadSecurity().catch(() => null).then(status => setWays(status === "signed_out" || status === null ? {
        code: true,
        passkey: true
      } : {
        code: status.totp,
        passkey: status.passkeys > 0
      }));
      dialog.current?.showModal();
    }));
    return () => onStepUp(null);
  }, []);
  function finish(passed: boolean) {
    dialog.current?.close();
    settle.current?.(passed);
    settle.current = null;
  }
  async function usePasskey() {
    setProblem(null);
    setBusy(true);
    const outcome = await signInWithPasskey(true).finally(() => setBusy(false));
    if (outcome === "ok") finish(true);else if (outcome !== "cancelled") setProblem(passkeyProblem(outcome, ways?.code ?? false));
  }
  return <dialog ref={dialog} aria-labelledby="step-up-heading" data-testid="step-up" onCancel={() => finish(false)} className="x17l03o0 xq24xx7 xmkeg23 x1y0btm7 xtkjqb x1gzjth6 xcqm4px xzb3lse x1cikjda x8xb0n0 x1j13seq">
      <div className="x78zum5 xdt5ytf x1r0x45p" aria-busy={ways === null}>
        <h2 id="step-up-heading" className="x1ghz6dp x3cmmws xqu410z">
          Confirm it's you
        </h2>
        <p role="status" className="x1ghz6dp x1jty25b x1fzhlzt">
          You asked to change your clinic's staff. {choice(ways)}
        </p>
        {ways?.passkey === true ? <Button tone={ways.code ? "quiet" : "solid"} busy={busy} onClick={() => void usePasskey()}>
            Use a passkey
          </Button> : null}
        {ways?.code === true ? <CodeForm submitLabel="Confirm" acceptsBackupCodes check={verifySecondFactor} onPassed={() => finish(true)} /> : null}
        {problem === null ? null : <p role="alert" className="x1ghz6dp x6ygkrl">
            {problem}
          </p>}
        <Button tone="quiet" onClick={() => finish(false)}>
          Don't change staff
        </Button>
      </div>
    </dialog>;
}